/**
 * @file Blocks route changes and tab closes while unsaved work exists.
 *
 * Mounted once by `RootLayout`. Renders nothing and deliberately holds no
 * Valtio subscription: both consumers are event-time and read the raw
 * `pendingSavesState` proxy when they fire, so per-keystroke `pendingIds` /
 * `dirtyIds` churn never re-renders the app root. The `beforeunload` listener
 * is registered unconditionally; the browser prompts only when the handler
 * calls `preventDefault`, which it does only when the raw proxy shows
 * unsaved work.
 */
import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';
import { pendingSavesState } from '../store/pendingSavesStore';

const hasUnsavedChanges = () =>
  pendingSavesState.pendingIds.size > 0 || pendingSavesState.dirtyIds.size > 0;

export default function NavigationGuard() {
  useBlocker(({ currentLocation, nextLocation }) => {
    if (!hasUnsavedChanges()) return false;
    if (currentLocation.pathname === nextLocation.pathname) return false;

    return !window.confirm(
      'You have unsaved changes. Are you sure you want to leave this page?'
    );
  });

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges()) return;
      event.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return null;
}
